import React from 'react';
import * as d3 from 'd3';
import {geoPath} from 'd3-geo';
import _ from 'lodash';
import { colord } from 'colord';

import ChoroplethTooltip from './ChoroplethTooltip';
import ChoroplethLegend from './ChoroplethLegend';
import ChoroplethDangerousPatternDefiner from './ChoroplethDangerousPatternDefiner';

export default class Choropleth extends React.Component {
  ASPECT_RATIO = 1.45;
  MAX_ZOOM_FACTOR = 36;
  LIGHTENING_FACTOR = 0.8;
  NUMBER_OF_BINS = 5;
  MAX_REGION_OPACTIY = 1.0;
  MIN_REGION_OPACITY = 0.4;
  TRANSITION_DURATION = 1000;
  INITIAL_STATE = {
    activeRegion: null,
    activeSubRegion: null
  };

  constructor(props) {
    super(props);
    //Object properties we use later
    this.svg            = null;
    this.path           = null;
    this.projection     = null;
    this.zoomContainer  = null;
    this.zoom           = null;
    this.width          = null;
    this.height         = null;

    //State
    this.state = this.INITIAL_STATE;

    this.svgRef = React.createRef();
  };

  //Life cycle methods
  componentDidMount = () => {
    [this.width, this.height] = this.calculateDimensions();
    this.svg = d3.select(this.getSVG());

    this.projection = this.props.projection;
    this.path = geoPath(this.projection);

    this.drawBackground();
    this.drawMap();
  };

  componentWillUpdate = (nextProps) => {
    // if (this.needsReset(this.props, nextProps))
    //   this.resetStateToInitial();
    this.showActiveRegion();
  };

  componentDidUpdate = (prevProps, prevState) => {
    this.updateRegionsAndSubRegions(prevProps, prevState);
    // this.updateColors(this.needsReset(this.props, prevProps));
    this.zoomToActiveMapRegion();
  };

  handleKeyDown = (event, callback) => {
    if(event.keyCode === 13 || event.keyCode === 32) {
      event.preventDefault();
      callback();
    }
  }

  //Our update functions
  needsReset = (currentProps, otherProps) => {
    return (otherProps.activeCluster != currentProps.activeCluster)
      || (otherProps.activeCategory != currentProps.activeCategory)
  }

  updateRegionsAndSubRegions = () => {
    this.drawActiveRegion(); // Only enable if you are NOT rendering subregions

    // !!!!!!!! If you are rendering subregions, enable the following lines: !!!!!!!
    // this.hideActiveRegion();
    // this.drawSubRegions(); // This controls the rendering of sub regions.
    // this.drawActiveSubRegion(); // See above

    this.setRegionOpacity();
  };

  // updateColors = (needsUpdate) => {
  //   if (needsUpdate)
  //     this.paths.call(this.setColor);
  // };

  //Drawing functions
  drawMap = () => {
    this.zoomContainer = this.svg.select('g.zoom-container');
    this.zoom = this.getZoom();
    this.paths = this.drawRegions();

    // this.sortPaths(paths); ONLY FOR USER REGION PURPOSES
    this.setDefaultZoom();

    this.svg
    //.call(this.zoom) //THIS LINE ENABLES PANNING AND ZOOMING USING MOUSE
      .call(this.zoom)
      .on('click', this.stopPropogationOnDrag, true)
      .on("dblclick.zoom", null);
  };

  drawRegions = () => {
    const g = this.svg.select('g.regions');
    const regionData = this.props.regionData.features;
    return this.drawMapRegions(g, regionData, { callback: this.onRegionClick });
  };

  // drawSubRegions = () => {
  //   const g = this.svg.select('g.sub-regions');
  //   if (this.state.activeRegion) {
  //     var regionName = this.state.activeRegion.properties.PROVCODE
  //     var subRegions = this.props.subRegionData.find((regionHash) => {
  //       return regionHash.key == regionName;
  //     });
  //   } else {
  //     var subRegions = null;
  //   }
  //   return this.subRegions = this.drawMapRegions(g, subRegions && subRegions.values,
  //     { callback: this.onSubRegionClick });
  // };

  //The most important draw function! If you understand this one you're good to go.
  drawMapRegions = (g, regions, options) => {
    var mapRegions = g.selectAll('path')
      .data(regions || [], function(d) {
        return d.properties.name;
      });

    mapRegions.enter()
      .append('path')
      .attr('class', options.class || this.getRegionClass)
      .attr('data-region-name', this.getMapRegionName)
      .attr("d", this.path)
      .style("fill-opacity", 1.0)
      .call(this.setColor)
      .on("mousemove", this.onMapRegionMouseMove)
      .on("mouseout", this.onMapRegionMouseOut)
      .on("click", options.callback);

    mapRegions.exit().remove();
    return mapRegions;
  };

  drawBackground = () => {
    this.svg.select('rect.background')
    .attr('width', this.width)
    .attr('height', this.height)
    .on("click", this.onBackgroundClick);
  };

  drawActiveRegion = () => {
    const g = this.svg.select('g.active-regions')
    var data = this.state.activeRegion ? [this.state.activeRegion] : []
    var className = "active-region";
    if (this.getMapRegionName(this.state.activeRegion) === this.props.userSubRegionName)
      className += "user-region";
    return this.drawMapRegions(g, data, { callback: this.onRegionClick, class: className })
  };

  // drawActiveSubRegion = () => {
  //   const g = this.svg.select('g.active-regions')
  //   var data = this.state.activeSubRegion ? [this.state.activeSubRegion] : []
  //   var className = "active-region";
  //   if (this.getMapRegionName(this.state.activeSubRegion) === this.props.userSubRegionName) {
  //     className += "user-region";
  //   }
  //   return this.drawMapRegions(g, data, { callback: this.onSubRegionClick, class: className });
  // };

  //This can eventually be replaced with css if you want
  setColor = (path) => {
    return path.attr('fill', feature => {
      let mapRegionName = this.getMapRegionName(feature);
      let regionChoropletData = this.props.choroplethData.find(cd => cd.name === mapRegionName);
      let color = _.get(regionChoropletData, "color", "000000");

      if(regionChoropletData.lightenColor) {
        color = colord(color).lighten(regionChoropletData.lightenColor).toHex();
      }

      return color;
    }).attr('stroke', "#FFFFFF");
  };

  setRegionOpacity = () => {
    const activeRegion = this.state.activeRegion;
    const regions = this.svg.selectAll('g.regions > path.region')
    const fullOpacityRegions = activeRegion ? [activeRegion] : regions.data();

    const transparentPaths = regions.filter((d) => {
        return !fullOpacityRegions.includes(d);
      });

    const nonTransparentPaths = regions.filter((d) => {
        return fullOpacityRegions.includes(d);
      });

    nonTransparentPaths.transition()
      .duration(this.TRANSITION_DURATION)
      .call((region) => region.style('fill-opacity', this.MAX_REGION_OPACTIY));

    transparentPaths.transition()
      .duration(this.TRANSITION_DURATION)
      .call((region) => region.style('fill-opacity', this.MIN_REGION_OPACITY));
  };

  // hideActiveRegion = () => {
  //   return this.getActiveRegion().style('display', 'none');
  // };

  showActiveRegion = () => {
    return this.getActiveRegion().style('display', '');
  };

  sortPaths = (paths) => {
    //Sorts paths IN PLACE
    return paths.sort((a,b) => {
      let nameA = this.getMapRegionName(a);
      let nameB = this.getMapRegionName(b);
      if (nameA === this.props.userRegionName) {
        return 1;
      } else if (nameB == this.props.userRegionName) {
        return -1;
      } else {
        return 0;
      }
    });
  };

  //Initializers
  
  //calculation methods
  calculateDimensions = () => {
    var svg = this.getSVG();
    const currentWidth = svg.getBoundingClientRect().width;
    const height = currentWidth / this.ASPECT_RATIO;
    svg.setAttribute('height', height);
    return [currentWidth, height];
  };

  getBounds = (region) => {
    var bounds = this.path.bounds(region);
    var [width,height] = bounds[1].map((_, i) => {
      return bounds[1][i] - bounds[0][i];
    });
    var [centerX,centerY] = bounds[0].map((_, i) => {
      return (bounds[0][i] + bounds[1][i]) / 2
    });

    return [width, height, centerX, centerY];
  };

  //Mostly selection or finds
  getSVG = () => {
    return this.svgRef.current;
  };

  getRegion = (regionName) => {
    return this.props
      .graphData
      .geo_regions
      .find(region => {
        return region.name.toLowerCase() === regionName.toLowerCase()
      });
  };

  getMapRegionName = (region) => {
    return region ? region.properties.name : null;
  };

  getLocalizedMapRegionName = (region) => {
    return region.properties.name;
  };

  getRegionClass = (region) => {
    return this.getMapRegionName(region) === this.props.userRegionName ? "region user-region" : "region";
  };

  getActiveRegion = () => {
    var regionName = this.getMapRegionName(this.state.activeRegion);
    return this.svg.select(`path.region[data-region-name="${regionName}"]`);
  };

  getActiveRegionName = () => {
    const region = this.state.activeSubRegion || this.state.activeRegion;
    return this.getMapRegionName(region);
  };

  //Zooming methods
  getZoom = () => {
    return d3.zoom()
      .scaleExtent([this.minScale, this.maxScale])
      .on("zoom", this.onZoom);
  };

  setDefaultZoom = () => {
    // This calculates the zoom extent using the bounding box of ALL regions
    const rect = this.getSVG().querySelector('g.regions').getBoundingClientRect();
    const regionsWidth = rect.width;
    const regionsHeight = rect.height;

    // This translation is necessary to move the map to the center of the screen
    // (instead of the top left corner).
    this.defaultTranslation = [this.width / 2, this.height / 2];

    this.minScale = 0.9 * Math.min(this.width / regionsWidth, this.height / regionsHeight);
    this.maxScale = this.MAX_ZOOM_FACTOR * this.minScale;

    this.zoomContainer.call(
      this.zoom.transform, 
      d3
        .zoomIdentity
        .translate(this.defaultTranslation[0], this.defaultTranslation[1])
        .scale(this.minScale)
    );
  };

  zoomToMapRegion = (region) => {
    const [regionWidth,regionHeight,centerX,centerY] = this.getBounds(region);
    
    // const newScale = 0.9 * Math.min(this.maxScale, 1 / Math.max(regionWidth / this.width, regionHeight / this.height));
    const newMinWidthScale = this.width / regionWidth;
    const newMinHeightScale = this.height / regionHeight;
    let newScale = Math.min(this.maxScale, newMinWidthScale, newMinHeightScale);
    newScale = 0.9 * newScale;

    const newTranslateVector = [centerX,centerY].map((position, i) => {
      return this.defaultTranslation[i] - newScale * position;
    });

    this.zoomContainer
    .transition()
    .duration(this.TRANSITION_DURATION)
    .call(
      this.zoom.transform,
      d3.zoomIdentity
        .translate(newTranslateVector[0], newTranslateVector[1])
        .scale(newScale)
    )
  };

  zoomToActiveMapRegion = () => {
    // Zoom to either the activeSubRegion or the activeRegion, or reset the zoom
    // if neither are set.
    const activeRegion = this.state.activeRegion;
    const activeSubRegion = this.state.activeSubRegion;
    const region = activeSubRegion || activeRegion;
    if (!!region) {
      this.zoomToMapRegion(region)
    } else {
      this.resetZoom();
    }
  };

  resetZoom = () => {
    this.drawRegions();
    this.zoomContainer
    .transition()
    .duration(this.TRANSITION_DURATION)
    .call(
      this.zoom.transform,
      d3.zoomIdentity
        .translate(this.defaultTranslation[0], this.defaultTranslation[1])
        .scale(this.minScale)
    )
  };

  zoomIn = (event) => {
    this.changeZoom(Math.min(this.maxScale, this.zoom.scale() * 2));
    return false;
  };

  zoomOut = (event) => {
    this.changeZoom(Math.max(this.minScale, this.zoom.scale() * 0.5));
    return false;
  };

  changeZoom = (newScale) => {
    const scaleFactor = newScale / this.zoom.scale();
    const defaultTranslation = [this.width / 2, this.height / 2];
    const newTranslate = this.zoom.translate().map((vecComponent, i) => {
      return defaultTranslation[i] + (vecComponent - defaultTranslation[i]) * scaleFactor;
    });
    this.transitionSVG(this.zoom.translate(newTranslate).scale(newScale).event);
  };

  // transitionSVG = (d3Zoom, duration = this.TRANSITION_DURATION) => {
  //   return this.svg.transition().duration(duration)
  //     .call(d3Zoom);
  // };

  //Event Callbacks
  onZoom = ({ transform }) => {
    this.zoomContainer.select('.active-region').style("stroke-width", 1.5 / transform.k + "px");
    this.zoomContainer.style("stroke-width", 1 / transform.k + "px");
    this.zoomContainer.attr("transform", transform);
  };

  onRegionClick = (event, region) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.state.activeRegion === region) {
      this.setState({ activeRegion: null })
    } else {
      this.setState({
        activeRegion: region,
        activeSubRegion: null
      });
    }
  };

  onRegionKeyDown = (callback) => {
    // return (region) => {
    //   if (d3.event.keyCode == 13 || d3.event.keyCode == 32) {
    //     d3.event.preventDefault();
    //     d3.event.stopPropagation();
    //     callback(region);
    //   }
    // };
  };

  // onSubRegionClick = (region) => {
  //   // d3.event.preventDefault();
  //   // d3.event.stopPropagation();
  //   if (this.state.activeSubRegion === region) {
  //     this.setState({ activeSubRegion: null });
  //   } else {
  //     this.setState({ activeSubRegion: region });
  //   }
  // };

  onBackgroundClick = () => {
    this.resetStateToInitial();
  };

  onMapRegionMouseMove = (event, region) => {
    const choroplethDatum = this.props.choroplethData.find(cd => cd.name === region.properties.name);
    let color = choroplethDatum.color;

    d3.select(this.refs.hoverTooltip)
      .classed('hidden', false)
      .html(`
        <div class="hoverTooltip__region_name">
          ${ this.getLocalizedMapRegionName(region) }
        </div>
        <div class="hoverTooltip__prediction" style="color: ${ color }">
          ${ choroplethDatum.prediction.toUpperCase() }
        </div>
      `)
      .attr('style', 'left:' + (event.layerX + 15) + 'px; \
       top:' + (event.layerY - 35) + 'px');
  };

  onMapRegionMouseOut = () => {
    d3.select(this.refs.hoverTooltip).classed('hidden', true)
  };

  stopPropogationOnDrag = () => {
    // if (d3.event.defaultPrevented) d3.event.stopPropagation();
  };

  resetStateToInitial = () => {
    this.setState(this.INITIAL_STATE);
  };

  getPatternName = (binIndex) => {
    return "choropleth-pattern-" + binIndex;
  };

  render() {
    let localizedText   = this.props.localizedText;
    let legendText      = localizedText && localizedText.legend;
    let highlightLabel  = localizedText && localizedText.highlightLabel;
    // const patternNames = colorBins.map((bin, i) => this.getPatternName(i+1));
    return (
      <div className="Choropleth">
        <svg ref={ this.svgRef } className='map-svg'>
          {/*<ChoroplethDangerousPatternDefiner
            names={patternNames}
            accessibilityEnabled={this.props.accessibilityEnabled} 
          />*/}

          <rect className="background" />
          <g className="zoom-container">
            <g className="regions"></g>
            <g className="sub-regions"></g>
            <g className="active-regions"></g>
          </g>
        </svg>
        <div ref="hoverTooltip" className="hover-tooltip hidden"></div>

        <ChoroplethTooltip
          regionName={this.getActiveRegionName()}
          highlightLabel={highlightLabel}
        />

        {/* Zooming buttons */}
        <div className="map-zoom-buttons">
          <a
            role="button"
            tabIndex="0"
            className="map-zoom-button"
            onClick={ this.zoomIn }
            onKeyDown={ (event) => this.handleKeyDown(event, this.zoomIn)  }>
              +
          </a>
          <a
            role="button"
            tabIndex="0"
            className="map-zoom-button"
            onClick={this.zoomOut}
            onKeyDown={ (event) => this.handleKeyDown(event, this.zoomOut)  }>
              -
          </a>
        </div>

        {/*<ChoroplethLegend
          patternNames={patternNames}
          colorBins={this.calculateColorBins()}
          legendText={ legendText }
          accessibilityEnabled={this.props.accessibilityEnabled}
          activeCategory={ this.props.activeCategory }
        />*/}
      </div>
    );
  }
}
